import { useEffect, useState } from "react";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../providers/AuthProvider";
import { resolveApiBaseUrl } from "../config/api-base-url";
import {
  extractErrorMessage,
  normalizeBaseUrl,
  parseResponsePayload,
} from "../utils/apiPayload";
import { formatWallClockDateTime } from "../utils/wallClockDate";

type QueueStatus = "idle" | "loading" | "ready" | "empty" | "error";

interface MakeupRequest {
  id: number;
  studentName: string;
  lessonDate: string; // ISO 8601
  reason: string;
  status: "pending" | "approved" | "rejected";
}

interface MakeupsQueueResponse {
  requests: MakeupRequest[];
}

interface StaffMakeupDetails {
  id: string;
  status: string;
  offeredSlot: string | null;
  student: string | null;
  lessonNo: number | null;
  flowNo: string | null;
  displayTimeZone: string | null;
}

interface MakeupsQueueProps {
  initialCancelMakeupRequestId?: string | null;
}

const defaultApiBaseUrl = resolveApiBaseUrl();

const formatDate = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return isoDate;
  }
};

export const MakeupsQueue = ({
  initialCancelMakeupRequestId = null,
}: MakeupsQueueProps) => {
  const auth = useAuth();
  const [status, setStatus] = useState<QueueStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [requests, setRequests] = useState<MakeupRequest[]>([]);
  const [staffActionStatus, setStaffActionStatus] =
    useState<QueueStatus>("idle");
  const [staffActionMessage, setStaffActionMessage] = useState<string | null>(
    null,
  );
  const [staffMakeup, setStaffMakeup] = useState<StaffMakeupDetails | null>(
    null,
  );
  const [cancelStatus, setCancelStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  const baseUrl = normalizeBaseUrl(defaultApiBaseUrl);

  const loadRequests = async () => {
    if (auth.status !== "authenticated" || !auth.session) {
      setStatus("error");
      setErrorMessage(
        "Заявки на отработку недоступны: отсутствует активная сессия преподавателя.",
      );
      return;
    }

    setStatus("loading");
    setErrorMessage(null);

    try {
      const response = await fetch(`${baseUrl}/makeups/pending-for-teacher`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${auth.session.token}`,
        },
      });

      const payload = await parseResponsePayload(response);

      if (!response.ok) {
        if (response.status === 401) {
          setStatus("error");
          setErrorMessage("Unauthorized: session истекла или невалидна.");
          return;
        }

        setStatus("error");
        setErrorMessage(
          `Не удалось загрузить заявки на отработку (${response.status}): ${extractErrorMessage(payload, "Неизвестная ошибка")}`,
        );
        return;
      }

      if (!payload || typeof payload !== "object") {
        setStatus("error");
        setErrorMessage("API вернул пустой или некорректный payload.");
        return;
      }

      const queueData = payload as MakeupsQueueResponse;
      setRequests(queueData.requests ?? []);

      if (!queueData.requests?.length) {
        setStatus("empty");
        return;
      }

      setStatus("ready");
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const loadStaffMakeup = async (requestId: string) => {
    if (auth.status !== "authenticated" || !auth.session) {
      setStaffActionStatus("error");
      setStaffActionMessage(
        "Отработка недоступна: отсутствует активная сессия.",
      );
      return;
    }

    setStaffActionStatus("loading");
    setStaffActionMessage(null);

    try {
      const response = await fetch(
        `${baseUrl}/makeups/${encodeURIComponent(requestId)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.session.token}`,
          },
        },
      );

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        setStaffActionStatus("error");
        setStaffActionMessage(
          `Не удалось загрузить отработку (${response.status}): ${extractErrorMessage(payload, "Неизвестная ошибка")}`,
        );
        return;
      }

      if (!payload || typeof payload !== "object") {
        setStaffActionStatus("error");
        setStaffActionMessage("API вернул некорректную карточку отработки.");
        return;
      }

      const item = payload as Partial<StaffMakeupDetails>;
      setStaffMakeup({
        id: String(item.id || requestId),
        status: String(item.status || ""),
        offeredSlot:
          typeof item.offeredSlot === "string" ? item.offeredSlot : null,
        student: typeof item.student === "string" ? item.student : null,
        lessonNo: typeof item.lessonNo === "number" ? item.lessonNo : null,
        flowNo: typeof item.flowNo === "string" ? item.flowNo : null,
        displayTimeZone:
          typeof item.displayTimeZone === "string"
            ? item.displayTimeZone
            : null,
      });
      setStaffActionStatus("ready");
    } catch (error) {
      setStaffActionStatus("error");
      setStaffActionMessage(
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  const cancelStaffMakeup = async () => {
    if (!staffMakeup || auth.status !== "authenticated" || !auth.session) {
      return;
    }

    const confirmed = window.confirm(
      "Отменить запись на отработку? Заявка снова станет открытой.",
    );
    if (!confirmed) {
      return;
    }

    setCancelStatus("submitting");
    setStaffActionMessage(null);

    try {
      const response = await fetch(
        `${baseUrl}/makeups/${encodeURIComponent(staffMakeup.id)}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${auth.session.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "open",
            offeredSlot: "",
          }),
        },
      );

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        setCancelStatus("error");
        setStaffActionMessage(
          `Не удалось отменить отработку (${response.status}): ${extractErrorMessage(payload, "Неизвестная ошибка")}`,
        );
        return;
      }

      setCancelStatus("success");
      setStaffActionMessage(
        "Запись отменена. Заявка снова открыта для выбора нового слота.",
      );
      await loadStaffMakeup(staffMakeup.id);
      await loadRequests();
    } catch (error) {
      setCancelStatus("error");
      setStaffActionMessage(
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  useEffect(() => {
    void loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.session, auth.status]);

  useEffect(() => {
    if (initialCancelMakeupRequestId) {
      void loadStaffMakeup(initialCancelMakeupRequestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.session, auth.status, initialCancelMakeupRequestId]);

  return (
    <>
      <h2 className="content-title">Отработки</h2>
      <p className="hint">
        Заявки студентов на дополнительные занятия и отработку пропусков.
      </p>

      {status === "loading" ? (
        <LoadingSpinner label="Загружаем заявки на отработку..." />
      ) : null}

      {status === "error" ? (
        <div className="alert">
          <p className="alert-text">{errorMessage ?? "Неизвестная ошибка"}</p>
          <button
            type="button"
            className="action-button retry-button-danger"
            onClick={() => void loadRequests()}
          >
            Повторить попытку
          </button>
        </div>
      ) : null}

      {status === "empty" ? (
        <div className="empty-state">
          <div className="empty-state-icon" aria-hidden="true">
            📋
          </div>
          <p className="empty-state-text">Нет новых заявок на отработку.</p>
        </div>
      ) : null}

      {status === "ready" && requests.length > 0 ? (
        <div className="makeup-requests-list">
          {requests.map((request) => (
            <div key={request.id} className="makeup-request-card">
              <div className="student-name">{request.studentName}</div>
              <div className="lesson-date">
                Пропущенный урок: {formatDate(request.lessonDate)}
              </div>
              <div className="reason">Комментарий: {request.reason}</div>
              {request.status !== "pending" && (
                <span className="status-badge">{request.status}</span>
              )}
              {request.status === "pending" ? (
                <span className="status-badge">В очереди</span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {initialCancelMakeupRequestId &&
      (staffActionStatus === "loading" ||
        staffActionStatus === "error" ||
        staffMakeup) ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Отмена записи на отработку"
          className="modal-backdrop"
          onClick={() => setStaffMakeup(null)}
        >
          <div
            className="panel modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="compact-title">Отмена отработки</h3>

            {staffActionStatus === "loading" ? (
              <LoadingSpinner label="Загружаем отработку..." />
            ) : null}

            {staffActionStatus === "error" ? (
              <div className="alert">
                <p className="alert-text">
                  {staffActionMessage ?? "Не удалось загрузить отработку."}
                </p>
                <button
                  type="button"
                  className="action-button"
                  onClick={() =>
                    void loadStaffMakeup(initialCancelMakeupRequestId)
                  }
                >
                  Повторить
                </button>
              </div>
            ) : null}

            {staffMakeup ? (
              <>
                <p className="hint flush-top">
                  {staffMakeup.student || "Ученик"} · поток{" "}
                  {staffMakeup.flowNo || "—"} · урок{" "}
                  {staffMakeup.lessonNo ?? "—"}
                </p>
                <ul className="compact-list">
                  <li>Статус: {staffMakeup.status}</li>
                  <li>
                    Слот: {formatWallClockDateTime(staffMakeup.offeredSlot)}
                  </li>
                </ul>

                {staffActionMessage ? (
                  <p
                    className={
                      cancelStatus === "error"
                        ? "alert status-note"
                        : "booking-success status-note"
                    }
                  >
                    {staffActionMessage}
                  </p>
                ) : null}

                <div className="action-row">
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => void cancelStaffMakeup()}
                    disabled={
                      cancelStatus === "submitting" ||
                      staffMakeup.status !== "booked"
                    }
                  >
                    {cancelStatus === "submitting"
                      ? "Отменяем..."
                      : staffMakeup.status === "booked"
                        ? "Отменить отработку"
                        : "Уже не записан"}
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => setStaffMakeup(null)}
                    disabled={cancelStatus === "submitting"}
                  >
                    Закрыть
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};
